import type { ReactNode } from "react";

import type { CaseOverviewPageOut, CaseStatus } from "@/api";
import { Bone } from "@/components/Bone";
import { caseLineClass } from "@/components/CaseCells";
import { Figures } from "@/components/Figures";
import { SortDownIcon } from "@/components/icons";
import { formatMoney } from "@/lib/format";

import { RegisterRow } from "./RegisterRow";
import type { RegisterSort } from "./RegisterToolbar";
import styles from "./Register.module.css";

const COLUMNS = 5;
const SKELETON_ROWS = 6;

/**
 * The skeleton's cells in the real cells' classes and line boxes (fix round
 * 3): with the same classes they take the same grid areas as a card on a
 * phone (round 2's anonymous cells stacked three bone pairs down the left of
 * every card), and with the rows' own line boxes (`caseLineClass`) around
 * each bone they are exactly as tall as a line of text — so a skeleton row
 * is a real row's height in every tier, not merely near it. The stage's two
 * bones sit in the real cell's `.stage` grid, without a step: most Vorgänge
 * have none, and a card keeps its middle line's height without one.
 */
/** `StageChip`'s own box: an 11 px line at 1.5, plus 1 px padding and 1 px border above and below. */
const CHIP_HEIGHT = "calc(var(--text-xs) * 1.5 + 4px)";

/*
 * Widths in rem, not percent (UX gate round 2, #1): on a card the title and
 * its number, the name and its address, the chip and its word stand side by
 * side, sized by their content — a percentage of that is nothing, and the
 * card skeleton drew empty.
 */
const SKELETON: readonly { cell: string; wrap?: string; lines: readonly string[] }[] = [
  { cell: styles.cellCase ?? "", lines: ["10rem", "4.5rem"] },
  { cell: styles.cellCustomer ?? "", lines: ["7rem", "9rem"] },
  { cell: styles.cellStage ?? "", wrap: styles.stage ?? "", lines: ["5.5rem", "5rem"] },
  { cell: `${styles.cellVolume ?? ""} ${styles.right ?? ""}`, lines: ["4.5rem"] },
  { cell: styles.cellWhen ?? "", lines: ["5rem", "6.5rem"] },
];

function SortHead({
  label,
  fullLabel,
  value,
  sort,
  onSort,
  end = false,
  inert = false,
}: {
  label: string;
  /**
   * Read out in full even where the printed head is shorter (fix round 1,
   * M2): „Volumen (VK netto)" wrapped to two lines at container widths the
   * other four heads fit on one. `aria-label` carries the whole phrase
   * regardless — a visually-hidden text node concatenated onto the visible
   * one lost its separating space in the accessible-name computation
   * (`VolumenVK netto` and `Volumen(VK netto)` were both seen from this).
   */
  fullLabel?: string;
  value: RegisterSort;
  sort: RegisterSort;
  onSort: (sort: RegisterSort) => void;
  end?: boolean;
  /**
   * Nothing to sort (fix round 4, M7): over an empty frame the head is a
   * label, not two live buttons that would only reload the same nothing.
   */
  inert?: boolean;
}) {
  const active = sort === value;
  if (inert) {
    return (
      <th role="columnheader" scope="col" className={end ? styles.right : undefined} title={fullLabel}>
        {/* The button's own box, so the head keeps its height (40 px). */}
        <span className={`label ${styles.sortHead} ${styles.sortHeadInert}`}>{label}</span>
      </th>
    );
  }
  return (
    <th
      role="columnheader"
      scope="col"
      aria-sort={active ? "descending" : undefined}
      className={end ? styles.right : undefined}
    >
      <button
        type="button"
        className={`label ${styles.sortHead}`}
        aria-label={fullLabel}
        title={fullLabel}
        onClick={() => onSort(value)}
      >
        {label}
        {active ? <SortDownIcon size={10} /> : null}
      </button>
    </th>
  );
}

interface RegisterTableProps {
  /** `null` while the first answer is on its way — the table shows its skeleton. */
  page: CaseOverviewPageOut | null;
  /** A newer answer is loading behind the one on screen (`isPlaceholderData`). */
  refreshing: boolean;
  sort: RegisterSort;
  onSort: (sort: RegisterSort) => void;
  busy: boolean;
  onStage: (id: string, to: CaseStatus) => void;
  /** Shown inside the frame instead of rows: nothing yet, or nothing found. */
  empty?: ReactNode;
  /** More hits sit behind the current limit (fix round 1, M8). */
  canShowMore: boolean;
  onShowMore: () => void;
}

/**
 * What went into the foot's sum, in words. Only current amounts are summed
 * (the server's `calculatedCount`); an amount of an older Stand is counted
 * apart (`staleCount`, UX-Gutachten 23.10.2026, Aufgabe 1) — „Summe aus 4
 * kalkulierten" added a price that no longer held. Without a stale one the
 * words are what they were.
 */
function sumWords(page: CaseOverviewPageOut): string {
  const current = page.calculatedCount;
  const stale = page.staleCount;
  if (stale === 0) return current > 0 ? `Summe aus ${current} kalkulierten` : "noch keiner kalkuliert";
  const staleWords = `${stale} veraltet`;
  if (current === 0) return `keine aktuelle Kalkulation · ${staleWords}`;
  const currentWords = current === 1 ? "1 aktuellen Kalkulation" : `${current} aktuellen Kalkulationen`;
  return `Summe aus ${currentWords} · ${staleWords}`;
}

/**
 * The register as one dense table in a panel, the sum as its last row (D-6)
 * — not a figure in a box above it (CI §6). The sum is `totalVk` from the
 * server over every hit before the limit, and the footer says how many
 * calculated Vorgänge went in; nothing here adds. Head and foot stick to
 * the viewport while the page scrolls (UX ruling 4, F-2).
 */
export function RegisterTable({
  page,
  refreshing,
  sort,
  onSort,
  busy,
  onStage,
  empty,
  canShowMore,
  onShowMore,
}: RegisterTableProps) {
  const loading = page === null;
  const rows = page?.items ?? [];
  // UX gate round 1, #6/#14: with nothing calculated the foot says so in its
  // label — „noch keiner kalkuliert"; S-1's italic sentence over two columns
  // („noch nichts kalkuliert") read beside the rows' „noch nicht kalkuliert"
  // as two different things. The volume cell under it stays empty (round-4
  // screen check, R4-5): a dash beside „noch keiner kalkuliert" said the same
  // thing twice — on a card since UX gate round 3 (#12), now in the table too.
  const nothingCalculated = page !== null && page.calculatedCount === 0;
  const footText = page === null ? "" : `${rows.length} von ${page.totalCount} · ${sumWords(page)}`;

  return (
    <div className={styles.tableWrap}>
      <table
        role="table"
        className={styles.table}
        aria-label={loading ? "Vorgänge werden geladen" : "Meine Vorgänge"}
        aria-busy={loading ? true : undefined}
      >
        <colgroup>
          <col className={styles.colCase} />
          <col className={styles.colCustomer} />
          <col className={styles.colStage} />
          <col className={styles.colVolume} />
          <col className={styles.colWhen} />
        </colgroup>
        <thead role="rowgroup">
          <tr role="row">
            <th role="columnheader" scope="col" className="label">
              Vorgang
            </th>
            {/* `.cellCustomer` — its own cell class, not `.headCustomer` — so
                the same collapse rule (fix round 2, ruling a) reaches the
                head and the body in one place. */}
            <th role="columnheader" scope="col" className={`label ${styles.cellCustomer}`}>
              Kunde
            </th>
            <th role="columnheader" scope="col" className="label">
              Stadium
            </th>
            <SortHead
              label="Volumen"
              fullLabel="Volumen (VK netto)"
              value="volume"
              sort={sort}
              onSort={onSort}
              end
              inert={empty !== undefined}
            />
            <SortHead label="Zuletzt" value="activity" sort={sort} onSort={onSort} inert={empty !== undefined} />
          </tr>
        </thead>
        <tbody role="rowgroup" className={styles.body} aria-busy={refreshing ? true : undefined}>
          {loading ? (
            Array.from({ length: SKELETON_ROWS }, (_, row) => (
              <tr role="row" key={row} className={`${styles.row} ${styles.skeletonRow}`}>
                {SKELETON.map(({ cell, wrap, lines }, index) => {
                  const bones = lines.map((width, line) => {
                    // The stage's first line holds a chip, a pixel taller
                    // than a line of text — its bone is chip-tall too.
                    const chip = index === 2 && line === 0;
                    return (
                      <span
                        key={line}
                        className={`${line === 0 ? caseLineClass.one : caseLineClass.two} ${chip ? styles.boneChip : styles.boneLine}`}
                      >
                        <Bone width={width} height={chip ? CHIP_HEIGHT : line === 0 ? "0.75rem" : "0.625rem"} />
                      </span>
                    );
                  });
                  return (
                    <td role="cell" key={index} className={cell}>
                      {wrap !== undefined ? <div className={wrap}>{bones}</div> : bones}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : empty !== undefined ? (
            <tr role="row">
              <td role="cell" colSpan={COLUMNS} className={styles.emptyCell}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((item) => <RegisterRow key={item.id} item={item} busy={busy} onStage={onStage} />)
          )}
        </tbody>
        {page !== null && empty === undefined ? (
          <tfoot role="rowgroup">
            <tr role="row">
              <td role="cell" colSpan={3} className={styles.footLabel}>
                <Figures text={footText} />
              </td>
              <td role="cell" className={`${styles.right} ${styles.footSum}`}>
                {nothingCalculated ? null : <span className="num">{formatMoney(page.totalVk)}</span>}
              </td>
              <td role="cell" className={styles.footMore}>
                {/* M8: the limit, not the search, decided how many landed — offer the rest instead of a silent ceiling. */}
                {canShowMore ? (
                  <button type="button" className={styles.showMore} onClick={onShowMore}>
                    Weitere zeigen
                  </button>
                ) : null}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
